import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import LandingPage from "./pages/LandingPage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import MarketplacePage from "./pages/MarketplacePage";
import KnowledgeHubPage from "./pages/KnowledgeHubPage";
import MapsPage from "./pages/MapsPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

// Dashboards
import LandownerDashboard from "./pages/dashboards/LandownerDashboard";
import ConsortiumDashboard from "./pages/dashboards/ConsortiumDashboard";
import AdminDashboard from "./pages/dashboards/AdminDashboard";

// Consortium Modules
import AgreementsModule from "./pages/consortium/AgreementsModule";
import MeetingsModule from "./pages/consortium/MeetingsModule";
import VotingModule from "./pages/consortium/VotingModule";
import RFPModule from "./pages/consortium/RFPModule";
import ConsentModule from "./pages/consortium/ConsentModule";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Dashboard Router - redirects to role-specific dashboard
const DashboardRouter = () => {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  switch (user.role) {
    case "admin":
      return <Navigate to="/admin" replace />;
    case "developer":
      return <Navigate to="/developer" replace />;
    default:
      return <Navigate to="/landowner" replace />;
  }
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/knowledge-hub" element={<KnowledgeHubPage />} />
          <Route path="/maps" element={<MapsPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Dashboard Router */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            }
          />

          {/* Landowner Dashboard */}
          <Route
            path="/landowner/*"
            element={
              <ProtectedRoute allowedRoles={["landowner", "consortium_rep"]}>
                <LandownerDashboard />
              </ProtectedRoute>
            }
          />

          {/* Consortium Dashboard & Modules */}
          <Route
            path="/consortium/:consortiumId"
            element={
              <ProtectedRoute>
                <ConsortiumDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consortium/:consortiumId/agreements"
            element={
              <ProtectedRoute>
                <AgreementsModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consortium/:consortiumId/meetings"
            element={
              <ProtectedRoute>
                <MeetingsModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consortium/:consortiumId/voting"
            element={
              <ProtectedRoute>
                <VotingModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consortium/:consortiumId/rfps"
            element={
              <ProtectedRoute>
                <RFPModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consortium/:consortiumId/consent"
            element={
              <ProtectedRoute>
                <ConsentModule />
              </ProtectedRoute>
            }
          />

          {/* Admin Dashboard */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
